import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Building2, ArrowRight } from "lucide-react";
import { WeatherControl } from "@/components/WeatherControl";

export default function Platform() {
  const { switchOrg } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState(null);

  useEffect(() => {
    api
      .get("/platform/orgs")
      .then(({ data }) => setOrgs(data))
      .catch(() => setOrgs([]));
  }, []);

  const enter = async (id, name) => {
    const res = await switchOrg(id);
    if (res.ok) {
      toast.success(`Now viewing ${name}`);
      navigate("/dashboard");
    } else {
      toast.error(res.error || "Could not switch organization");
    }
  };

  if (!orgs) return <Loader2 className="h-6 w-6 animate-spin text-royal" />;

  return (
    <div className="space-y-6" data-testid="platform-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Cornerstone Ops Platform</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2">All Organizations</h1>
        <p className="text-muted-foreground mt-3 text-sm md:text-base">
          Every company on the platform. Entering an organization is recorded in its audit trail.
        </p>
      </div>

      <WeatherControl />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {orgs.map((o) => (
          <div
            key={o._id}
            data-testid={`platform-org-${o._id}`}
            className="rounded-lg border border-border bg-white p-5 hover:shadow-md hover:-translate-y-[2px] transition-transform duration-200"
          >
            <div className="flex items-center gap-2.5">
              <Building2 className="h-4 w-4 text-gold" />
              <p className="font-display font-semibold text-royal-deep truncate">{o.name}</p>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              {[
                ["Users", o.users],
                ["Properties", o.properties],
                ["Jobs", o.jobs],
              ].map(([k, v]) => (
                <div key={k} className="rounded-md bg-secondary/60 py-2">
                  <p className="font-display text-lg font-bold text-royal-deep leading-none">{v}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{k}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className="rounded-full border border-royal/15 bg-royal/5 px-2 py-0.5 text-[11px] font-semibold text-royal uppercase">
                {o.plan}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {(!o.plan_status || o.plan_status === "pre_trial")
                  ? ((o.plan || "trial") === "trial" ? "trial not started" : "active")
                  : o.plan_status.replace("_", " ")}
              </span>
            </div>
            <button
              data-testid={`enter-org-${o._id}`}
              onClick={() => enter(o._id, o.name)}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-md bg-royal px-3 min-h-[44px] text-xs font-semibold text-white hover:bg-royal-deep transition-colors duration-200"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Enter organization <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
